"use client"

import {
  IconTrophy,
  IconMedal,
  IconMedal2,
  IconUser,
} from "@tabler/icons-react"
import { useTranslations } from "next-intl"

import { Badge } from "@/components/ui/badge"
import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Skeleton } from "@/components/ui/skeleton"

import { type LeaderboardResponse } from "@/lib/targets-api"

interface LeaderboardWidgetProps {
  data: LeaderboardResponse | null
  isLoading: boolean
}

export function LeaderboardWidget({ data, isLoading }: LeaderboardWidgetProps) {
  const t = useTranslations("dashboard")
  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <IconTrophy className="h-5 w-5 text-yellow-500" />
      case 2:
        return <IconMedal className="h-5 w-5 text-gray-400" />
      case 3:
        return <IconMedal2 className="h-5 w-5 text-amber-700" />
      default:
        return (
          <span className="w-5 h-5 flex items-center justify-center text-xs font-medium text-muted-foreground">
            {rank}
          </span>
        )
    }
  }

  if (isLoading) {
    return (
      <ApolloPanel eyebrow={t("leaderboard.header")}>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-2">
              <Skeleton className="h-5 w-5 rounded" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-1.5 w-full rounded-full" />
              </div>
              <Skeleton className="h-4 w-12" />
            </div>
          ))}
        </div>
      </ApolloPanel>
    )
  }

  if (!data?.has_active_period || !data.leaderboard?.length) {
    return (
      <ApolloPanel eyebrow={t("leaderboard.header")}>
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <div className="rounded-full bg-muted p-3 mb-3">
            <IconMedal className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {t("leaderboard.empty.title")}
          </p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            {t("leaderboard.empty.body")}
          </p>
        </div>
      </ApolloPanel>
    )
  }

  return (
    <ApolloPanel
      eyebrow={t("leaderboard.header")}
      actions={
        data.period ? (
          <Badge variant="outline" className="text-[10px]">
            {data.period.period_label}
          </Badge>
        ) : undefined
      }
    >
      <div className="space-y-2">
        {data.leaderboard.map((entry) => (
          <div
            key={entry.rank}
            className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
              entry.is_current_user
                ? "bg-primary/10 border border-primary/20"
                : "hover:bg-muted/50"
            }`}
          >
            {/* Rank */}
            <div className="w-6 flex justify-center">
              {getRankIcon(entry.rank)}
            </div>

            {/* User + Progress Bar */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <IconUser className="h-3.5 w-3.5 text-muted-foreground" />
                <span
                  className={`text-sm truncate ${
                    entry.is_current_user ? "font-semibold" : "font-medium"
                  }`}
                >
                  {entry.user.full_name}
                </span>
                {entry.is_current_user && (
                  <Badge variant="secondary" className="text-[10px] px-1">
                    {t("leaderboard.you")}
                  </Badge>
                )}
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden mt-1">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.min(entry.percentage, 100)}%`,
                    backgroundColor: entry.percentage >= 100 ? 'var(--chart-2)' : entry.percentage >= 80 ? 'var(--chart-4)' : 'var(--chart-1)'
                  }}
                />
              </div>
            </div>

            {/* Progress */}
            <div className="text-right">
              <span
                className={`text-sm font-semibold tabular-nums ${
                  entry.percentage >= 100
                    ? "text-green-600"
                    : entry.percentage >= 80
                    ? "text-yellow-600"
                    : ""
                }`}
              >
                {entry.percentage.toFixed(1)}%
              </span>
            </div>

            {/* Tier Badge */}
            {entry.tier_achieved && (
              <Badge variant="outline" className="text-[10px] shrink-0">
                ${entry.tier_achieved.bonus_amount}
              </Badge>
            )}
          </div>
        ))}
      </div>
    </ApolloPanel>
  )
}
